import { EPaymentFrequency, EPaymentPlan } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import { ActionList, Caption, Icon } from '@if/mui'
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { Check, CircleAlert, CircleCheck, Star } from 'lucide-react'
import { useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import { useEditCreditApplication } from '@src/data/api/credit-api/credit-api'
import { useGetPersonalFunding } from '@src/data/api/worksheet-api/personal-loan-worksheet-api'
import { getDisplayedMaxAmountFinanced } from '@src/data/creditapp-selectors'
import { ComputeFundingDto } from '@src/types/ComputePersonalLoanFundingDto'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { useApplicablePromoPlans } from '@src/data/api/merchants-api/merchants-api'
import { ResolvedPaymentPlan } from '@src/types/ResolvedPaymentPlan'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adjustCreditApplicationYearsForApi } from '../../credit-hooks'

interface Props {
  onApplied: () => void
  creditApp: CreditApplication
  merchant: Merchant
}

interface PlanRowProps {
  plan: ResolvedPaymentPlan | null
  label: string
  termMonths: number
  stars: number | undefined
  computeBase: Omit<ComputeFundingDto, 'paymentPlanId' | 'requestId'> | null
  creditAppId: string
  maxAmount: number
  maxPmt: number
  onApply: (promoCode: string) => void
  isApplying: boolean
}

const PlanRow = ({
  plan,
  label,
  termMonths,
  stars,
  computeBase,
  creditAppId,
  maxAmount,
  maxPmt,
  onApply,
  isApplying,
}: PlanRowProps) => {
  const { t } = useTranslation()
  const planId = plan?.id ?? (EPaymentPlan.RegularDailyInterests as string)

  const computeDto = useMemo(() => {
    if (!computeBase) return null
    return { ...computeBase, paymentPlanId: planId, requestId: crypto.randomUUID(), term: termMonths }
  }, [computeBase, planId, termMonths])

  const [fundingResult, isComputing] = useGetPersonalFunding(creditAppId, computeDto, maxAmount)
  const isValid = fundingResult && fundingResult.paymentForFrequency <= maxPmt
  const term = termMonths

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Caption>{label}</Caption>
          {stars != null && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              ({stars}
              <Icon icon={Star} size="xs" />
              {stars < 5 && '+'})
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell align="right">{term && <Caption>{`${term} ${t('common.months')}`}</Caption>}</TableCell>
      <TableCell align="right">
        <Caption>{plan?.merchantFeeRate != null ? `${plan.merchantFeeRate}%` : '—'}</Caption>
      </TableCell>
      <TableCell align="right">
        {isComputing ? (
          <CircularProgress size={18} />
        ) : fundingResult ? (
          <Caption>{formatCurrency(fundingResult.paymentForFrequency)}</Caption>
        ) : null}
      </TableCell>
      <TableCell align="center">
        {!isComputing &&
          fundingResult &&
          (isValid ? (
            <Icon icon={CircleCheck} color="success" size="md" />
          ) : (
            <Icon icon={CircleAlert} color="error" size="md" />
          ))}
      </TableCell>
      <TableCell align="right">
        {isValid && (
          <ActionList
            actions={[
              {
                icon: Check,
                label: t('viewCreditApplication.applyPlan'),
                disabled: isApplying,
                onClick: () => onApply(plan?.label ?? ''),
              },
            ]}
            variant="button"
            menuLabel={t('viewCreditApplication.applyPlan')}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

const PaymentPlanSimulator = ({ onApplied, creditApp, merchant }: Props) => {
  const { t } = useTranslation()
  const [financingConfig] = useMerchantFinancingConfig(merchant.id, creditApp.financingProgramId)
  const maxAmount =
    getDisplayedMaxAmountFinanced(
      creditApp.financingProgramId,
      creditApp.finalCreditDecision.maxAmountFinanced,
      financingConfig,
    ) ?? 0
  const maxPmt = creditApp.finalCreditDecision.maxPmtAmount
  const maxTerm = creditApp.finalCreditDecision.maxTermDuration ?? 60

  const [amountRequested, setAmountRequested] = useState<number>(creditApp.requestedLoanAmount ?? maxAmount)
  const [editCreditApp, isEditing] = useEditCreditApplication()

  const [applicablePromoPlans] = useApplicablePromoPlans(merchant.id)

  const promoCodeMinStars = useMemo(() => {
    const map = new Map<string, number>()
    if (!merchant?.planPromoRules) return map
    for (const rule of merchant.planPromoRules) {
      if (!/^\*+$/.test(rule.planName)) continue
      const stars = rule.planName.length
      const codes = [rule.defaultPromoCode, ...(rule.selectablePromoCodes ?? [])]
      for (const code of codes) {
        const current = map.get(code)
        if (current === undefined || stars < current) map.set(code, stars)
      }
    }
    return map
  }, [merchant?.planPromoRules])

  const isAmountValid = amountRequested > 0 && amountRequested <= maxAmount
  const today = formatDate(new Date())
  const firstPaymentOn = formatDate(new Date(new Date().setDate(new Date().getDate() + 30)))

  const computeBase = useMemo(() => {
    if (!isAmountValid) return null
    return {
      creditApplicationId: creditApp.id,
      amountRequested,
      paymentFrequency: EPaymentFrequency.Monthly,
      term: maxTerm,
      includeInsurance: false,
      includeProtection: false,
      interestRate: creditApp.finalCreditDecision.interestRate,
      hasCoapplicant: creditApp.coApplicant !== null,
      stateIso: creditApp.applicant.currentAddress.stateIso!,
      deliveryOn: today,
      firstPaymentOn,
      merchantId: creditApp.merchantId,
    }
  }, [isAmountValid, amountRequested, maxTerm, creditApp, today, firstPaymentOn])

  const handleApply = async (promoCode: string) => {
    const data = { ...creditApp, prequalPromoCode: promoCode || undefined, requestedLoanAmount: amountRequested }
    adjustCreditApplicationYearsForApi(data)
    await editCreditApp(data)
    onApplied()
  }

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 2 }} alignItems="center">
        <Caption sx={{ whiteSpace: 'nowrap' }}>
          {t('viewCreditApplication.maxMonthlyPayment')}: <strong>{formatCurrency(maxPmt)}</strong>
        </Caption>
        <TextField
          label={t('editCreditApplication.amountRequested')}
          type="number"
          value={amountRequested}
          onChange={(e) => setAmountRequested(Number(e.target.value))}
          error={!isAmountValid}
          helperText={!isAmountValid ? `Max: ${formatCurrency(maxAmount)}` : undefined}
          slotProps={{ htmlInput: { min: 0, max: maxAmount, step: 100 } }}
          size="small"
          fullWidth
        />
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('worksheet.paymentPlan')}</TableCell>
              <TableCell align="right">{t('viewCreditApplication.maxTerm')}</TableCell>
              <TableCell align="right">{t('viewCreditApplication.merchantFee')}</TableCell>
              <TableCell align="right">{t('viewCreditApplication.monthlyPayment')}</TableCell>
              <TableCell align="center" />
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            <PlanRow
              plan={null}
              label={t('common.regularPlan')}
              termMonths={maxTerm}
              stars={undefined}
              computeBase={computeBase}
              creditAppId={creditApp.id}
              maxAmount={maxAmount}
              maxPmt={maxPmt}
              onApply={handleApply}
              isApplying={isEditing}
            />
            {applicablePromoPlans.flatMap((plan) => {
              const promoCode = plan.label?.replace('PLAN', '').trim() ?? ''
              const minStars = promoCodeMinStars.get(plan.label ?? '') ?? promoCodeMinStars.get(promoCode)
              return plan.termRates.map((rate) => (
                <PlanRow
                  key={`${plan.id}-${rate.termMonths}`}
                  plan={plan}
                  label={plan.label ?? ''}
                  termMonths={rate.termMonths}
                  stars={minStars}
                  computeBase={computeBase}
                  creditAppId={creditApp.id}
                  maxAmount={maxAmount}
                  maxPmt={maxPmt}
                  onApply={handleApply}
                  isApplying={isEditing}
                />
              ))
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default PaymentPlanSimulator
